import { useRef } from "react";
import "./Tabs.css";

/*
  Tabs Component

  The tab strip only (the panel stays with the caller): WAI-ARIA tab pattern
  with roving tabindex and arrow / Home / End keys. Arrow direction follows
  the reading direction (`rtl`).

  tabs      [{ key, label }]
  value     key of the selected tab
  onChange  called with the new key
  label     accessible name of the tablist
  idPrefix  ids are `${idPrefix}-tab-${key}`; the panel should use
            id `${idPrefix}-panel-${key}` and aria-labelledby the tab id
*/

const Tabs = ({ tabs = [], value, onChange, label, idPrefix = "ui", rtl = false, className = "" }) => {
  const refs = useRef({});

  const onKeyDown = (event) => {
    const index = tabs.findIndex((tab) => tab.key === value);
    let next = null;
    if (event.key === "ArrowRight") next = rtl ? index - 1 : index + 1;
    else if (event.key === "ArrowLeft") next = rtl ? index + 1 : index - 1;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = tabs.length - 1;
    if (next === null) return;
    event.preventDefault();
    const target = tabs[(next + tabs.length) % tabs.length];
    onChange?.(target.key);
    refs.current[target.key]?.focus();
  };

  return (
    <div className={`ui-tabs ${className}`} role="tablist" aria-label={label} onKeyDown={onKeyDown}>
      {tabs.map((tab) => (
        <button
          key={tab.key}
          ref={(el) => { refs.current[tab.key] = el; }}
          id={`${idPrefix}-tab-${tab.key}`}
          type="button"
          role="tab"
          aria-selected={value === tab.key}
          aria-controls={`${idPrefix}-panel-${tab.key}`}
          tabIndex={value === tab.key ? 0 : -1}
          onClick={() => onChange?.(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
};

export default Tabs;
